import React from 'react';
import {
  Container, Row, Col, Table, Badge, Button, Modal, Form,
  Spinner, Alert, Card, Collapse,
} from 'react-bootstrap';
import Header from '../common/Header';
import { useEmailTemplates } from './hooks/useEmailTemplates';

const EmailTemplates: React.FC = () => {
  const {
    templates,
    loadingTemplates,
    error,
    selectedTemplate,
    showEditor,
    openEditor,
    closeEditor,
    form,
    updateField,
    saving,
    saveError,
    saveSuccess,
    handleSave,
    previewHtml,
    showPreview,
    setShowPreview,
    loadingPreview,
    handlePreview,
    testEmail,
    setTestEmail,
    showTestModal,
    setShowTestModal,
    sendingTest,
    testSuccess,
    setTestSuccess,
    testError,
    setTestError,
    handleSendTest,
    logs,
    loadingLogs,
    showLogs,
    toggleLogs,
    availableVariables,
    insertVariable,
    setActiveField,
    subjectRef,
    bodyHtmlRef,
    bodyTextRef,
  } = useEmailTemplates();

  return (
    <>
    <Header />
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h2>Email Templates</h2>
          <p className="text-muted">Manage and customize the emails sent to your customers.</p>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Templates Table */}
      {loadingTemplates ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <Card className="mb-4">
          <Card.Body className="p-0">
            <Table hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Template</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Last Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {templates.map(t => (
                  <tr key={t.id}>
                    <td className="fw-semibold">{t.trigger_display || t.trigger}</td>
                    <td className="text-muted">{t.subject}</td>
                    <td>
                      <Badge bg={t.is_active ? 'success' : 'secondary'}>
                        {t.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>{new Date(t.updated_at).toLocaleDateString()}</td>
                    <td>
                      <Button size="sm" variant="outline-primary" onClick={() => openEditor(t)}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
                {templates.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      No email templates found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Email Logs */}
      <Card>
        <Card.Header
          onClick={toggleLogs}
          style={{ cursor: 'pointer' }}
          className="d-flex justify-content-between align-items-center"
        >
          <span>Email Logs</span>
          <Badge bg="light" text="dark">{showLogs ? '▲' : '▼'}</Badge>
        </Card.Header>
        <Collapse in={showLogs}>
          <div>
            <Card.Body className="p-0">
              {loadingLogs ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : logs.length === 0 ? (
                <p className="text-muted text-center py-4 mb-0">No email logs yet.</p>
              ) : (
                <Table hover responsive size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Trigger</th>
                      <th>To</th>
                      <th>Subject</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <tr key={log.id}>
                        <td>{new Date(log.sent_at).toLocaleString()}</td>
                        <td>{log.template_trigger}</td>
                        <td>{log.to_email}</td>
                        <td>{log.subject}</td>
                        <td>
                          <Badge bg={log.status === 'sent' ? 'success' : log.status === 'failed' ? 'danger' : 'warning'}>
                            {log.status}
                          </Badge>
                          {log.error_message && (
                            <small className="d-block text-danger">{log.error_message}</small>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      {/* Editor Modal */}
      <Modal show={showEditor} onHide={closeEditor} size="xl" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            Edit Template: {selectedTemplate?.trigger_display || selectedTemplate?.trigger}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {saveError && <Alert variant="danger" dismissible onClose={() => {}}>{saveError}</Alert>}
          {saveSuccess && <Alert variant="success" dismissible onClose={() => {}}>{saveSuccess}</Alert>}

          <Row>
            {/* Editor Column */}
            <Col md={availableVariables.length > 0 ? 9 : 12}>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Subject</Form.Label>
                  <Form.Control
                    ref={subjectRef as any}
                    type="text"
                    value={form.subject}
                    onChange={e => updateField('subject', e.target.value)}
                    onFocus={() => setActiveField('subject')}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>HTML Body</Form.Label>
                  <Form.Control
                    ref={bodyHtmlRef as any}
                    as="textarea"
                    rows={14}
                    value={form.body_html}
                    onChange={e => updateField('body_html', e.target.value)}
                    onFocus={() => setActiveField('body_html')}
                    style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Plain Text Body <small className="text-muted">(optional fallback)</small></Form.Label>
                  <Form.Control
                    ref={bodyTextRef as any}
                    as="textarea"
                    rows={5}
                    value={form.body_text}
                    onChange={e => updateField('body_text', e.target.value)}
                    onFocus={() => setActiveField('body_text')}
                    style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                  />
                </Form.Group>

                <Row>
                  <Col md={5}>
                    <Form.Group className="mb-3">
                      <Form.Label>From Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={form.from_name}
                        onChange={e => updateField('from_name', e.target.value)}
                        placeholder="e.g. My Bakery"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group className="mb-3">
                      <Form.Label>Reply-To</Form.Label>
                      <Form.Control
                        type="email"
                        value={form.reply_to}
                        onChange={e => updateField('reply_to', e.target.value)}
                        placeholder="e.g. support@mybakery.com"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2} className="d-flex align-items-center pt-2">
                    <Form.Check
                      type="switch"
                      id="template-active"
                      label="Active"
                      checked={form.is_active}
                      onChange={e => updateField('is_active', e.target.checked)}
                    />
                  </Col>
                </Row>
              </Form>
            </Col>

            {/* Variables Sidebar */}
            {availableVariables.length > 0 && (
              <Col md={3}>
                <Card className="bg-light">
                  <Card.Header className="py-2">
                    <small className="fw-semibold">Available Variables</small>
                  </Card.Header>
                  <Card.Body className="py-2">
                    <p className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
                      Click to insert into the focused field.
                    </p>
                    <div className="d-flex flex-wrap gap-1">
                      {availableVariables.map(v => (
                        <Badge
                          key={v}
                          bg="primary"
                          className="fw-normal"
                          style={{ cursor: 'pointer', fontSize: '0.8rem' }}
                          onClick={() => insertVariable(v)}
                        >
                          {`{{${v}}}`}
                        </Badge>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>

          {/* Preview */}
          {showPreview && previewHtml && (
            <Card className="mt-3">
              <Card.Header className="d-flex justify-content-between align-items-center py-2">
                <span>Preview</span>
                <Button size="sm" variant="outline-secondary" onClick={() => setShowPreview(false)}>
                  Close Preview
                </Button>
              </Card.Header>
              <Card.Body className="p-0">
                <div
                  style={{ minHeight: 300, padding: '1rem' }}
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              </Card.Body>
            </Card>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <div className="d-flex gap-2">
            <Button
              variant="outline-info"
              onClick={handlePreview}
              disabled={loadingPreview}
            >
              {loadingPreview ? <Spinner size="sm" animation="border" className="me-1" /> : null}
              Preview
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => setShowTestModal(true)}
            >
              Send Test Email
            </Button>
          </div>
          <div className="d-flex gap-2">
            <Button variant="secondary" onClick={closeEditor}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? <Spinner size="sm" animation="border" className="me-1" /> : null}
              Save Changes
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Test Email Modal */}
      <Modal show={showTestModal} onHide={() => { setShowTestModal(false); setTestSuccess(''); setTestError(''); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Send Test Email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {testSuccess && <Alert variant="success">{testSuccess}</Alert>}
          {testError && <Alert variant="danger">{testError}</Alert>}
          <Form.Group>
            <Form.Label>Recipient Email</Form.Label>
            <Form.Control
              type="email"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              placeholder="test@example.com"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTestModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSendTest} disabled={sendingTest || !testEmail}>
            {sendingTest ? <Spinner size="sm" animation="border" className="me-1" /> : null}
            Send
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
    </>
  );
};

export default EmailTemplates;
